import { useMemo, useState } from 'react'
import { Search, ChevronDown, X } from 'lucide-react'
import { clsx } from 'clsx'
import { NIGERIAN_SERVICE_AREAS } from '@lib/constants/nigerianServiceAreas'

interface ServiceAreaSelectProps {
  value: string
  onChange: (value: string) => void
  includeAllOption?: boolean
  allOptionLabel?: string
  label?: string
  className?: string
}

export default function ServiceAreaSelect({
  value,
  onChange,
  includeAllOption = true,
  allOptionLabel = 'All Locations',
  label = 'Location',
  className,
}: ServiceAreaSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const options = useMemo(() => {
    const base = includeAllOption ? [allOptionLabel, ...NIGERIAN_SERVICE_AREAS] : [...NIGERIAN_SERVICE_AREAS]
    const query = search.trim().toLowerCase()
    if (!query) return base
    return base.filter((option) => option.toLowerCase().includes(query))
  }, [allOptionLabel, includeAllOption, search])

  const displayValue = value || allOptionLabel

  return (
    <div className={clsx('relative', className)}>
      {label && (
        <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
      )}
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground hover:bg-muted transition-colors"
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown size={16} className="text-muted-foreground shrink-0" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close location picker"
            className="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false)
              setSearch('')
            }}
          />
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search locations..."
                  className="w-full pl-8 pr-8 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {options.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No locations found</li>
              ) : (
                options.map((option) => (
                  <li key={option}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(option === allOptionLabel ? 'All' : option)
                        setOpen(false)
                        setSearch('')
                      }}
                      className={clsx(
                        'w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors',
                        displayValue === option && 'bg-primary/10 text-primary font-medium'
                      )}
                    >
                      {option}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
